import { Typography } from "@povio/ui";
import type { Header, Table } from "@tanstack/react-table";
import { cx } from "class-variance-authority";
import type { PropsWithChildren } from "react";

import { ArrowDropDownIcon } from "@/assets/icons/general/ArrowDropDown";
import { Button } from "@/components/ui/buttons/Button/Button";
import { Dropdown } from "@/components/ui/inputs/Dropdown/Dropdown";

interface IProps extends PropsWithChildren {
  header: Header<any, unknown>;
  label?: string;
  align?: "start" | "center" | "end";
  table: Table<any>;
}

const WatchlistTableHeaderCell = ({ header, label, align = "start", table }: IProps) => {
  const allColumns = table?.getAllColumns();
  const visibleColumns = allColumns?.filter((column) => column.getIsVisible());
  const hiddenColumns = allColumns?.filter((column) => !column.getIsVisible());

  // Get the current column order from the table
  const currentColumnOrder = table?.getState().columnOrder || [];

  // Sort visible columns according to the current column order
  const sortedVisibleColumns = visibleColumns?.sort((a, b) => {
    const aIndex = currentColumnOrder.indexOf(a.id);
    const bIndex = currentColumnOrder.indexOf(b.id);

    if (aIndex !== -1 && bIndex !== -1) {
      return aIndex - bIndex;
    }

    if (aIndex !== -1) return -1;
    if (bIndex !== -1) return 1;

    return 0;
  });

  const isLastVisibleColumn = sortedVisibleColumns?.[sortedVisibleColumns.length - 1]?.id === header?.column.id;
  return (
    <div
      {...{
        className: cx(
          "flex items-center gap-1 px-2-5 py-1 min-w-10",
          align === "start" && "justify-start",
          align === "center" && "justify-center",
          align === "end" && "justify-end",
        ),
      }}
    >
      {isLastVisibleColumn && !!hiddenColumns?.length ? (
        <Dropdown
          label=""
          placeholder=""
          hideLabels
          items={
            hiddenColumns?.map((column) => ({
              label: (column.columnDef.meta as any)?.label || "",
              value: column.id,
              id: column.id,
            })) || []
          }
          value={header?.column.id}
          onChange={(value) => {
            let newColumnOrder = [...(table.getState().columnOrder || [])];
            if (currentColumnOrder.length === 0) {
              newColumnOrder = [...(allColumns?.map((column) => column.id) || [])];
            }
            const headerid = header?.column.id;
            const currentHeaderIndex = newColumnOrder.indexOf(headerid || "");
            const selectedColumnIndex = newColumnOrder.indexOf(value);

            if (currentHeaderIndex !== -1 && selectedColumnIndex !== -1) {
              const selectedColumnId = newColumnOrder[selectedColumnIndex];
              const currentHeaderId = newColumnOrder[currentHeaderIndex];

              newColumnOrder[selectedColumnIndex] = currentHeaderId;
              newColumnOrder[currentHeaderIndex] = selectedColumnId;
            }
            setTimeout(() => {
              table?.setColumnOrder([...newColumnOrder]);
            }, 100);

            table?.getColumn(value)?.toggleVisibility();
            table?.getColumn(header.column.id)?.toggleVisibility();
          }}
          customTrigger={
            <Button
              className="relative mr-4! w-auto bg-transparent! p-0!"
              variant="primary"
              size="small"
              icon={ArrowDropDownIcon}
              iconClassName="absolute -right-3 top-0 bottom-0 m-auto w-4 h-4 text-text-default-1!"
            >
              <Typography
                size="label-1"
                variant={header?.column.getIsSorted() ? "prominent-1" : "default"}
                className="text-text-default-1"
                as="span"
              >
                {label}
              </Typography>
            </Button>
          }
        />
      ) : (
        <Typography
          size="label-1"
          variant={header?.column.getIsSorted() ? "prominent-1" : "default"}
          className="relative text-text-default-1"
          as="span"
        >
          {label}
        </Typography>
      )}
    </div>
  );
};

export default WatchlistTableHeaderCell;
